import React, { useCallback, useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { startOfDay } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { fetchIv_list } from '../../../store';
import { fetchAndBuildHourlyGeneration } from './services/hourlyGenerationService';
import ReportToolbar from './components/ReportToolbar';
import HourlyChartComponent from './components/HourlyChartComponent';
import HourlyTableComponent from './components/HourlyTableComponent';
import {
  HOURLY_PERIOD_OPTIONS,
  HOURLY_PERIOD_RANGES,
} from './utils/reportPeriodRanges';
import { MAX_HOURLY_RANGE_DAYS } from './utils/hourlyChartUtils';
import './GenHourly.css';

const HOURLY_CSV_HEADERS = [
  { label: '시간', key: 'Time' },
  { label: '인버터', key: 'Inverter' },
  { label: '인버터출력 (kW)', key: 'GridPower' },
  // AddPowerKwh: API Add_power_1h (1시간 구간 증분, Wh→kWh)
  { label: '1시간 발전량 (kWh)', key: 'AddPowerKwh' },
  { label: '측정시각', key: 'created_at' },
  { label: 'PV 총전압 (V)', key: 'PvTotalVolt' },
  { label: 'PV 총전류 (A)', key: 'PvTotalCurrent' },
  { label: 'PV 출력 (kW)', key: 'PvPower' },
  { label: '계통 RS전압 (V)', key: 'Grid_RS_Volt' },
  { label: '계통 ST전압 (V)', key: 'Grid_ST_Volt' },
  { label: '계통 TR전압 (V)', key: 'Grid_TR_Volt' },
];

const GenHourly = () => {
  const dispatch = useDispatch();
  const ivList = useSelector((state) => state.Iv_list);

  const [startDate, setStartDate] = useState(startOfDay(new Date()));
  const [endDate, setEndDate] = useState(startOfDay(new Date()));
  const [activePeriod, setActivePeriod] = useState('today');

  const [tempViewMode, setTempViewMode] = useState('total');
  const [tempDisplayType, setTempDisplayType] = useState('chart');
  const [viewMode, setViewMode] = useState('total');
  const [displayType, setDisplayType] = useState('chart');

  const [displayedData, setDisplayedData] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [tooltipBySlot, setTooltipBySlot] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchIv_list());
  }, [dispatch]);

  const runSearch = useCallback(async (searchStart = startDate, searchEnd = endDate) => {
    try {
      setIsLoading(true);
      const result = await fetchAndBuildHourlyGeneration({
        startDate: searchStart,
        endDate: searchEnd,
        mode: tempViewMode,
        ivList,
      });

      setDisplayedData(result.chartData);
      setCsvData(result.csvData);
      setTooltipBySlot(result.tooltipBySlot);
      setViewMode(tempViewMode);
      setDisplayType(tempDisplayType);
    } catch (error) {
      console.error(error);
      alert(error.message || '데이터를 가져오는 중 오류가 발생했습니다.');
      setDisplayedData(null);
      setCsvData([]);
      setTooltipBySlot([]);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, tempViewMode, tempDisplayType, ivList]);

  const handleSearch = useCallback(() => {
    runSearch(startDate, endDate);
  }, [runSearch, startDate, endDate]);

  const applyPeriod = useCallback(async (periodKey) => {
    const range = HOURLY_PERIOD_RANGES[periodKey]();
    setStartDate(range.start);
    setEndDate(range.end);
    setActivePeriod(range.key);
    await runSearch(range.start, range.end);
  }, [runSearch]);

  return (
    <div className="gen-hourly-page">
      <ReportToolbar
        periodOptions={HOURLY_PERIOD_OPTIONS}
        activePeriod={activePeriod}
        onPeriodClick={applyPeriod}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        pickerMode="date"
        maxRangeDays={MAX_HOURLY_RANGE_DAYS}
        rangeHint="조회 기간은 최대 1주일(7일)까지 선택할 수 있습니다."
        tempViewMode={tempViewMode}
        setTempViewMode={setTempViewMode}
        tempDisplayType={tempDisplayType}
        setTempDisplayType={setTempDisplayType}
        handleSearch={handleSearch}
        isLoading={isLoading}
        csvData={csvData}
        csvFilename="hourly_generation.csv"
        csvHeaders={HOURLY_CSV_HEADERS}
        onDateChange={() => setActivePeriod(null)}
      />

      {displayType === 'chart' ? (
        <div className="hourly-chart-wrapper">
          <HourlyChartComponent
            displayedData={displayedData}
            viewMode={viewMode}
            tooltipBySlot={tooltipBySlot}
          />
        </div>
      ) : (
        <HourlyTableComponent csvData={csvData} />
      )}
    </div>
  );
};

export default GenHourly;
